import React, { useState, useEffect } from "react";
import { Button, Box, CircularProgress, IconButton, Tooltip, Menu, MenuItem, Badge } from "@mui/material";
import { Edit, Refresh, Save, Delete, SwapHoriz, AttachFile, AttachmentOutlined, Block } from "@mui/icons-material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { updateRFQ, checkManualMoveStatus } from "../../../../api/client";
import { toast } from "../../../../utils/notifications";
import { useUser } from "../../../../contexts/UserContext";
import { TEAM_MEMBERS } from "../../../../config/team";
import { useAttachmentCount } from "../../../../hooks";
import { queryKeys } from "../../../../api/queryKeys";
import type { ErrorLike } from "../../../../utils/errors";

interface StatusActionsProps {
  rfqId: number;
  status: "NEW" | "DIY" | "QUOTED";
  allItemsValidated: boolean;
  onDelete: () => void;
  onViewAttachments: (rfqId: number) => void;
  onBlacklist?: () => void;
}

export default function StatusActions({ rfqId, status, allItemsValidated, onDelete, onViewAttachments, onBlacklist }: StatusActionsProps) {
  const [isUpdating, setIsUpdating] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [hasCheckedFailure, setHasCheckedFailure] = useState(false);
  const queryClient = useQueryClient();
  const { selectedUser } = useUser();
  const { data: attachmentCount = 0, isLoading: isLoadingAttachments } = useAttachmentCount(rfqId);

  // Automatically check for permanent email move failures for DIY RFQs
  useEffect(() => {
    let failureCheckInterval: NodeJS.Timeout;

    if (status === "DIY" && !hasCheckedFailure) {
      // Check immediately after a short delay
      const initialCheck = setTimeout(() => {
        checkForPermanentFailure();
      }, 3000); // Wait 3 seconds for the queued move task to be recorded

      // Then check every 30 seconds for up to 5 minutes (for retries)
      failureCheckInterval = setInterval(() => {
        checkForPermanentFailure();
      }, 30000);

      // Stop checking after 5 minutes
      setTimeout(() => {
        clearInterval(failureCheckInterval);
        setHasCheckedFailure(true);
      }, 5 * 60 * 1000);

      return () => {
        clearTimeout(initialCheck);
        clearInterval(failureCheckInterval);
      };
    }
  }, [status, hasCheckedFailure, rfqId]);

  const checkForPermanentFailure = async () => {
    try {
      const statusResponse = await checkManualMoveStatus(rfqId);
      
      if (statusResponse.success && statusResponse.permanentlyFailed) {
        // Email move has permanently failed after all retries
        console.error(`Email move permanently failed for RFQ ${rfqId}:`, statusResponse);
        
        toast({
          title: "Email Move Failed",
          description: `Failed to move email to "Moved To Manual" folder after ${statusResponse.maxAttempts} attempts. Please check your email server connection or move the email manually.`,
          severity: "error",
        });

        // Stop checking once we've notified about permanent failure
        setHasCheckedFailure(true);
        
      } else if (statusResponse.success && statusResponse.status === 'COMPLETED') {
        // Email move completed successfully, stop checking
        setHasCheckedFailure(true);
      }
    } catch (error) {
      console.error(`Error checking manual move status for RFQ ${rfqId}:`, error);
      // Don't show error toast for status check failures to avoid spam
    }
  };

  const handleReassignClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleReassignClose = () => {
    setAnchorEl(null);
  };

  const handleReassignToUser = (userName: string) => {
    if (userName === selectedUser?.name) {
      toast({
        title: "Invalid Selection",
        description: "RFQ is already assigned to this user",
        severity: "warning",
      });
      return;
    }

    updateStatusMutation.mutate({
      moved_to_manual_by: userName,
      moved_to_manual_at: new Date().toISOString()
    });
  };

  // Status update mutation
  const updateStatusMutation = useMutation({
    mutationFn: (data: {
      status?: string;
      moved_to_manual_by?: string;
      moved_to_manual_at?: string;
      moved_to_active_by?: string;
      moved_to_active_at?: string;
    }) => {
      setIsUpdating(true);
      return updateRFQ(rfqId, data);
    },
    onSuccess: async () => {
      // Invalidate the query to force a refetch
      queryClient.invalidateQueries({ queryKey: queryKeys.rfqs.all });

      setIsUpdating(false);
      handleReassignClose();
      toast({
        title: "Success",
        description: "RFQ status updated successfully",
        severity: "success",
      });

      // The DIY status change queues the email move; the worker performs it
    },
    onError: (error: ErrorLike) => {
      console.error(`Failed to update RFQ ${rfqId}:`, error);
      setIsUpdating(false);
      handleReassignClose();
      
      // Extract more detailed error information
      const errorMessage = error.message || '';
      const statusCode = error.response?.status || '';
      const errorData = error.response?.data?.error || '';
      
      toast({
        title: "Error Updating RFQ Status",
        description: `Failed to update RFQ status: ${errorMessage || errorData || 'Server communication error'}${statusCode ? ` (Status: ${statusCode})` : ''}. Please try again or contact support.`,
        severity: "error",
      });
    },
  });

  return (
    // display: contents lets the parent row lay out (and wrap) these buttons with its siblings
    <Box sx={{ display: "contents" }}>
      {/* View Attachments Button - Show for all statuses */}
      <Tooltip 
        title={
          isLoadingAttachments ? 'Loading attachment information...' :
          attachmentCount > 0 ? `View ${attachmentCount} attachment${attachmentCount !== 1 ? 's' : ''} for this RFQ` : 
          'No attachments found for this RFQ'
        }
        arrow
      >
        <span>
          <Button
            variant={attachmentCount > 0 ? "contained" : "outlined"}
            size="small"
            onClick={() => onViewAttachments(rfqId)}
            startIcon={
              isLoadingAttachments ? (
                <CircularProgress size={16} />
              ) : (
                <Badge 
                  badgeContent={attachmentCount > 0 ? attachmentCount : null}
                  color="error"
                  sx={{ 
                    '& .MuiBadge-badge': { 
                      fontSize: '0.75rem',
                      minWidth: '18px',
                      height: '18px',
                      padding: '0 2px'
                    }
                  }}
                >
                  {attachmentCount > 0 ? <AttachFile /> : <AttachmentOutlined />}
                </Badge>
              )
            }
            sx={{ 
              
              color: attachmentCount > 0 ? 'white' : 'text.secondary',
              backgroundColor: attachmentCount > 0 ? 'primary.main' : 'transparent',
              borderColor: attachmentCount > 0 ? 'primary.main' : 'action.disabled',
              '&:hover': {
                backgroundColor: attachmentCount > 0 ? 'primary.dark' : 'action.hover',
                borderColor: attachmentCount > 0 ? 'primary.dark' : 'action.hover',
              },
              whiteSpace: 'nowrap',
              minWidth: 'fit-content'
            }}
            disabled={isLoadingAttachments}
          >
            {isLoadingAttachments ? 'Loading...' : 
             attachmentCount > 0 ? `${attachmentCount} Attachment${attachmentCount !== 1 ? 's' : ''}` : 'No Attachments'}
          </Button>
        </span>
      </Tooltip>

      {status === "NEW" && (
        <Button
          variant="outlined"
          size="small"
          onClick={async () => {
            if (!selectedUser?.name) {
              toast({
                title: "Error",
                description: "Please select a user at the top of the page before moving to manual",
                severity: "error",
              });
              return;
            }
            
            // Show immediate feedback
            toast({
              title: "Moving to Manual",
              description: "Queueing the email move...",
              severity: "info",
            });

            // The status change to DIY queues the email move for the worker
            updateStatusMutation.mutate({
              status: "DIY",
              moved_to_manual_by: selectedUser.name,
              moved_to_manual_at: new Date().toISOString(),
              moved_to_active_by: undefined,
              moved_to_active_at: undefined
            });
          }}
          startIcon={<Edit />}
          disabled={isUpdating}
          sx={{ whiteSpace: 'nowrap' }}
        >
          Move to Manual
        </Button>
      )}

      {status === "DIY" && (
        <>
          <Button
            size="small"
            onClick={() => updateStatusMutation.mutate({
              status: "NEW",
              moved_to_active_by: selectedUser?.name,
              moved_to_active_at: new Date().toISOString()
            })}
            startIcon={<Refresh />}
            disabled={isUpdating}
            variant="outlined"
            sx={{ whiteSpace: 'nowrap' }}
          >
            Back to Active
          </Button>

          <Button
            size="small"
            onClick={handleReassignClick}
            startIcon={<SwapHoriz />}
            disabled={isUpdating}
            variant="outlined"
            sx={{ whiteSpace: 'nowrap' }}
          >
            Reassign
          </Button>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleReassignClose}
          >
            {TEAM_MEMBERS.map((member) => (
              <MenuItem 
                key={member.name}
                onClick={() => handleReassignToUser(member.name)}
                disabled={member.name === selectedUser?.name}
              >
                {member.name}
              </MenuItem>
            ))}
          </Menu>

          {allItemsValidated && (
            <Button
              variant="contained"
              size="small"
              color="primary"
              onClick={() => {
                updateStatusMutation.mutate({
                  status: "QUOTED",
                  moved_to_active_by: selectedUser?.name,
                  moved_to_active_at: new Date().toISOString()
                });
              }}
              startIcon={<Save />}
              disabled={isUpdating}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Mark as Quoted
            </Button>
          )}
        </>
      )}

      {/* Blacklist button */}
      {onBlacklist && (
        <Tooltip title="Blacklist this sender">
          <IconButton
            size="small"
            color="warning"
            onClick={onBlacklist}
            disabled={isUpdating}
          >
            <Block />
          </IconButton>
        </Tooltip>
      )}

      {/* Delete button */}
      <Tooltip title="Delete RFQ">
        <IconButton
          size="small"
          color="error"
          onClick={onDelete}
          disabled={isUpdating}
        >
          <Delete />
        </IconButton>
      </Tooltip>
    </Box>
  );
}
